import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Lock, RefreshCw, LogOut, Package, CircleDollarSign, Scale, ShoppingBag } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { fmtCOP } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPedidos = () => {
  const [password, setPassword] = useState('');
  const [autenticado, setAutenticado] = useState(!!sessionStorage.getItem('fp_admin'));
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(false);
  const [pedidos, setPedidos] = useState([]);
  const [stats, setStats] = useState(null);
  const [filtro, setFiltro] = useState('paid');

  const cargar = useCallback(async () => {
    const pass = sessionStorage.getItem('fp_admin');
    if (!pass) return;
    setCargando(true);
    try {
      const { data } = await axios.get(`${API}/admin/orders`, {
        headers: { 'X-Admin-Password': pass },
      });
      setPedidos(data.pedidos || []);
      setStats(data.stats || null);
      setError('');
    } catch (e) {
      if (e?.response?.status === 401) {
        sessionStorage.removeItem('fp_admin');
        setAutenticado(false);
      }
      setError('No se pudieron cargar los pedidos');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    if (autenticado) cargar();
  }, [autenticado, cargar]);

  const entrar = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem('fp_admin', password);
      setAutenticado(true);
      setPassword('');
    } catch (err) {
      setError('Contraseña incorrecta');
    }
  };

  const salir = () => {
    sessionStorage.removeItem('fp_admin');
    setAutenticado(false);
    setPedidos([]);
    setStats(null);
  };

  const visibles = filtro === 'paid' ? pedidos.filter((p) => p.payment_status === 'paid') : pedidos;

  const estadoBadge = (s) => {
    if (s === 'paid') return <span className="badge-estado pagado">Pagado</span>;
    if (s === 'expired' || s === 'failed') return <span className="badge-estado fallido">{s === 'expired' ? 'Expirado' : 'Fallido'}</span>;
    return <span className="badge-estado pendiente">Pendiente</span>;
  };

  if (!autenticado) {
    return (
      <>
        <Header />
        <div className="checkout-page">
          <div className="checkout-card" style={{ maxWidth: 400 }}>
            <div className="checkout-icon wait"><Lock size={34} /></div>
            <h1 style={{ fontSize: '1.5rem' }}>Panel de pedidos</h1>
            <p>Acceso privado de Finca Pescado.</p>
            <form onSubmit={entrar} style={{ display: 'grid', gap: 12, marginTop: 14 }}>
              <input
                className="field"
                type="password"
                placeholder="Contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password"
              />
              {error && <p style={{ color: 'var(--acento-osc)', fontSize: '.88rem' }}>{error}</p>}
              <button type="submit" className="btn btn-stripe" style={{ marginTop: 0 }} data-testid="admin-entrar">Entrar</button>
            </form>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <section className="admin" style={{ minHeight: '70vh' }}>
        <div className="wrap">
          <div className="admin-top">
            <div>
              <p className="eyebrow">Panel privado</p>
              <h2 className="sec">Pedidos</h2>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-stripe" style={{ width: 'auto', marginTop: 0, padding: '10px 18px' }} onClick={cargar} disabled={cargando}>
                <RefreshCw size={18} className={cargando ? 'animate-spin' : ''} /> Actualizar
              </button>
              <button className="btn" style={{ background: 'var(--linea)', color: 'var(--tinta)', padding: '10px 18px' }} onClick={salir}>
                <LogOut size={18} /> Salir
              </button>
            </div>
          </div>

          {stats && (
            <div className="admin-stats">
              <div className="stat"><ShoppingBag size={20} /><b>{stats.pagados}</b><span>Pedidos pagados</span></div>
              <div className="stat"><CircleDollarSign size={20} /><b>{fmtCOP(stats.ingresos_cop)}</b><span>Ingresos (COP)</span></div>
              <div className="stat"><Scale size={20} /><b>{stats.kilos_vendidos} kg</b><span>Kilos vendidos</span></div>
              <div className="stat"><Package size={20} /><b>{stats.total_pedidos}</b><span>Pedidos totales</span></div>
            </div>
          )}

          <div className="opt-row" style={{ margin: '18px 0' }}>
            <button className={`chip${filtro === 'paid' ? ' sel' : ''}`} onClick={() => setFiltro('paid')}>Pagados</button>
            <button className={`chip${filtro === 'all' ? ' sel' : ''}`} onClick={() => setFiltro('all')}>Todos</button>
          </div>

          <div className="admin-tabla-wrap">
            <table className="admin-tabla" data-testid="admin-tabla">
              <thead>
                <tr>
                  <th>Fecha</th><th>N°</th><th>Cliente</th><th>Teléfono</th><th>Entrega</th><th>Zona</th><th>Detalle</th><th>Kilos</th><th>Domicilio</th><th>Total</th><th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {visibles.length === 0 && (
                  <tr><td colSpan="11" style={{ textAlign: 'center', color: 'var(--tinta-2)', padding: 24 }}>No hay pedidos {filtro === 'paid' ? 'pagados' : ''} todavía</td></tr>
                )}
                {visibles.map((p) => (
                  <tr key={p.id}>
                    <td>{p.created_at ? new Date(p.created_at).toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</td>
                    <td>{(p.id || '').slice(0, 8).toUpperCase()}</td>
                    <td>{p.nombre || '—'}</td>
                    <td>{p.telefono || '—'}</td>
                    <td>{[p.direccion, p.ciudad].filter(Boolean).join(', ') || '—'}</td>
                    <td>{p.zona_label || '—'}</td>
                    <td>{(p.lineas || []).map((l) => `${l.talla} ${l.kg}kg`).join(', ')}</td>
                    <td>{p.total_kg} kg</td>
                    <td>{p.domicilio_cop > 0 ? fmtCOP(p.domicilio_cop) : 'Gratis'}</td>
                    <td style={{ fontWeight: 700 }}>{fmtCOP(p.total_cop)}</td>
                    <td>{estadoBadge(p.payment_status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default AdminPedidos;
